import type { LucideIcon } from 'lucide-react';
import {
  BarChart2,
  BarChart3,
  Briefcase,
  Building,
  CircleDot,
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  Database,
  FileText,
  Flag,
  Globe2,
  HelpCircle,
  History,
  LayoutDashboard,
  MessageSquare,
  RotateCcw,
  Settings,
  Shield,
  ShieldAlert,
  Trophy,
  User,
  UserCircle,
  UserPlus,
  Users,
  Users2,
} from 'lucide-react';

import { ROUTES } from './routes';

export interface NavItem {
  label: string;
  icon: LucideIcon;
  href: string;
  badge?: string;
}

export interface SidebarTheme {
  gradient: string;
  borderColor: string;
  navTextColor: string;
  navHoverBg: string;
  activeColor: string;
  badgeBg: string;
  userCardBg: string;
  userGradientFrom: string;
  userGradientTo: string;
  logoIcon: LucideIcon;
  logoBg: string;
  subtitle: string;
  userName: string;
  userOrg: string;
  userInitials: string;
}

export interface SidebarConfig {
  theme: SidebarTheme;
  navItems: NavItem[];
  secondaryItems: NavItem[];
}

export const organizerSidebar: SidebarConfig = {
  theme: {
    gradient: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
    borderColor: 'border-slate-700',
    navTextColor: 'text-slate-400',
    navHoverBg: 'hover:bg-white/10',
    activeColor: 'bg-blue-600',
    badgeBg: 'bg-blue-600',
    userCardBg: 'bg-slate-800/50',
    userGradientFrom: 'from-blue-500',
    userGradientTo: 'to-indigo-600',
    logoIcon: CircleDot,
    logoBg: 'bg-blue-600',
    subtitle: 'Organisateur',
    userName: '',
    userOrg: 'Organisateur',
    userInitials: '',
  },
  navItems: [
    { label: 'Tableau de bord', icon: LayoutDashboard, href: ROUTES.ORGANIZER_DASHBOARD },
    { label: 'Mes concours', icon: Trophy, href: ROUTES.COMPETITION_LIST },
    { label: 'Inscriptions', icon: Users2, href: ROUTES.ORGANIZER_INSCRIPTIONS },
    { label: 'Clubs affilies', icon: Flag, href: ROUTES.ORGANIZER_CLUBS },
    { label: 'Paiements', icon: CreditCard, href: ROUTES.ORGANIZER_PAYMENTS },
    { label: 'Demandes de remb.', icon: RotateCcw, href: ROUTES.ORGANIZER_REFUND_REQUESTS },
    { label: 'Statistiques', icon: BarChart3, href: ROUTES.ORGANIZER_ANALYTICS },
    { label: 'Demandes admin', icon: UserPlus, href: ROUTES.ORGANIZER_ADMIN_REQUESTS },
    { label: 'Blog', icon: FileText, href: ROUTES.ORGANIZER_BLOG },
    { label: 'Mon site', icon: Globe2, href: ROUTES.ORGANIZER_SITE_CMS },
  ],
  secondaryItems: [
    { label: 'Profil organisateur', icon: Building, href: ROUTES.ORGANIZER_PROFILE_EDIT },
    { label: 'Parametres', icon: Settings, href: ROUTES.ORGANIZER_SETTINGS },
    { label: 'Aide & support', icon: HelpCircle, href: import.meta.env.VITE_URL_FRONT || 'http://localhost:5232' },
  ],
};

export const clubSidebar: SidebarConfig = {
  theme: {
    gradient: 'linear-gradient(180deg, #065f46 0%, #064e3b 100%)',
    borderColor: 'border-emerald-700',
    navTextColor: 'text-emerald-200',
    navHoverBg: 'hover:bg-white/10',
    activeColor: 'bg-emerald-500',
    badgeBg: 'bg-emerald-600',
    userCardBg: 'bg-emerald-800/50',
    userGradientFrom: 'from-emerald-500',
    userGradientTo: 'to-green-600',
    logoIcon: Flag,
    logoBg: 'bg-emerald-600',
    subtitle: 'Gestionnaire Club',
    userName: '',
    userOrg: 'Mon club',
    userInitials: '',
  },
  navItems: [
    { label: 'Tableau de bord', icon: LayoutDashboard, href: ROUTES.CLUB_DASHBOARD },
    { label: 'Membres', icon: Users, href: ROUTES.CLUB_MEMBERS },
    { label: 'Equipes', icon: Users2, href: ROUTES.CLUB_TEAMS },
    { label: 'Concours disponibles', icon: Trophy, href: ROUTES.CLUB_COMPETITIONS },
    { label: 'Mes inscriptions', icon: ClipboardCheck, href: ROUTES.CLUB_INSCRIPTIONS },
    { label: 'Organisateurs', icon: Briefcase, href: ROUTES.CLUB_ORGANIZERS },
    { label: 'Historique', icon: History, href: ROUTES.CLUB_HISTORY },
    { label: 'Statistiques', icon: BarChart3, href: ROUTES.CLUB_ANALYTICS },
    { label: 'Blog', icon: FileText, href: ROUTES.CLUB_BLOG },
    { label: 'Mon site', icon: Globe2, href: ROUTES.CLUB_SITE_CMS },
  ],
  secondaryItems: [
    { label: 'Profil du club', icon: Building, href: ROUTES.CLUB_PROFILE_EDIT },
    { label: 'Parametres', icon: Settings, href: ROUTES.CLUB_SETTINGS },
    {
      label: 'Aide & support',
      icon: HelpCircle,
      href: `${import.meta.env.VITE_URL_FRONT}/support`,
    },
  ],
};

export const playerSidebar: SidebarConfig = {
  theme: {
    gradient: 'linear-gradient(180deg, #c2410c 0%, #9a3412 100%)',
    borderColor: 'border-orange-700',
    navTextColor: 'text-orange-200',
    navHoverBg: 'hover:bg-white/10',
    activeColor: 'bg-orange-500',
    badgeBg: 'bg-orange-600',
    userCardBg: 'bg-orange-800/50',
    userGradientFrom: 'from-orange-500',
    userGradientTo: 'to-red-600',
    logoIcon: User,
    logoBg: 'bg-orange-600',
    subtitle: 'Espace Joueur',
    userName: '',
    userOrg: 'Joueur',
    userInitials: '',
  },
  navItems: [
    { label: 'Tableau de bord', icon: LayoutDashboard, href: ROUTES.PLAYER_DASHBOARD },
    { label: 'Mes inscriptions', icon: ClipboardList, href: ROUTES.PLAYER_INSCRIPTIONS },
    { label: 'Mon club', icon: Flag, href: ROUTES.PLAYER_CLUB },
    { label: 'Historique', icon: History, href: ROUTES.PLAYER_HISTORY },
    { label: 'Mes statistiques', icon: BarChart2, href: ROUTES.PLAYER_STATS },
  ],
  secondaryItems: [
    { label: 'Mon profil', icon: UserCircle, href: ROUTES.PLAYER_PROFILE },
    { label: 'Preferences', icon: Settings, href: ROUTES.PLAYER_PREFERENCES },
    { label: 'Aide & support', icon: HelpCircle, href: `${import.meta.env.VITE_URL_FRONT}/support` as string },
  ],
};

export const adminSidebar: SidebarConfig = {
  theme: {
    gradient: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
    borderColor: 'border-slate-800',
    navTextColor: 'text-slate-300',
    navHoverBg: 'hover:bg-slate-800',
    activeColor: 'bg-red-600',
    badgeBg: 'bg-red-600',
    userCardBg: 'bg-slate-800/50',
    userGradientFrom: 'from-red-500',
    userGradientTo: 'to-orange-600',
    logoIcon: ShieldAlert,
    logoBg: 'bg-gradient-to-br from-red-600 to-orange-600',
    subtitle: 'BoulesConnect',
    userName: '',
    userOrg: 'Acces total',
    userInitials: '',
  },
  navItems: [
    { label: 'Dashboard', icon: LayoutDashboard, href: ROUTES.ADMIN_DASHBOARD },
    { label: 'Utilisateurs', icon: Users, href: ROUTES.ADMIN_USERS },
    { label: 'Clubs', icon: Building, href: ROUTES.ADMIN_CLUBS },
    { label: 'Organisateurs', icon: Briefcase, href: ROUTES.ADMIN_ORGANIZERS },
    { label: 'Concours', icon: Trophy, href: ROUTES.ADMIN_COMPETITIONS },
    { label: 'Blog', icon: FileText, href: ROUTES.ADMIN_BLOG },
    { label: 'Paiements', icon: CreditCard, href: ROUTES.ADMIN_PAYMENTS },
    { label: 'Demandes de remb.', icon: RotateCcw, href: ROUTES.ADMIN_REFUND_REQUESTS },
    { label: 'Feedbacks', icon: MessageSquare, href: ROUTES.ADMIN_FEEDBACK },
    { label: 'Statistiques', icon: BarChart2, href: ROUTES.ADMIN_ANALYTICS },
    { label: 'RGPD Traitements', icon: Shield, href: ROUTES.ADMIN_GDPR_TREATMENTS },
    { label: 'RGPD Violations', icon: ShieldAlert, href: ROUTES.ADMIN_GDPR_BREACHES },
    // Monitoring queues BullMQ — dev/test uniquement (backend BullBoard absent en prod)
    ...(import.meta.env.DEV ? [{ label: 'Queues', icon: Database, href: ROUTES.ADMIN_QUEUES }] : []),
  ],
  secondaryItems: [{ label: 'Parametres', icon: Settings, href: ROUTES.ADMIN_SETTINGS }],
};
